import React from 'react';
import { DebugRecord, OfferDebugData } from '~/purchase/types';
// eslint-disable-next-line @wyz/module/no-relative-import
import './style.scss';
import { FormatDate, Icon } from '@wyz/ui';
import { DEBUG_PRICING_SOURCE_LOOKUP_LABEL } from '~/purchase/translators';
import { Trans } from '~/common/context';
import { isNil } from 'lodash-es';
import { clsx } from 'clsx';
interface DebugTableProps {
  records: Record<string, DebugRecord>;
}

const CURRENCY = '€';

const ColoredPrice: React.FC<{ price: number | undefined }> = ({ price }) => {
  if (isNil(price)) {
    // eslint-disable-next-line @wyz/i18n/no-plain-text
    return <span>-</span>;
  }
  const isNegative = price < 0;
  const colorClass = isNegative ? 'text-red' : 'text-green-1';
  return (
    <span className={clsx(colorClass, 'font-bold')}>
      {price.toFixed(2)}
      {CURRENCY}
    </span>
  );
};

export const DebugTable: React.FC<DebugTableProps> = ({ records }) => {
  const recordsArray = Object.entries(records).map(([ean, record]) => ({
    ...record,
    ean,
  }));

  if (recordsArray.length === 0) {
    return (
      <div className='diagnosis-table__empty'>
        <Trans
          i18nKey='app.common.datatable.count_result_found'
          values={{ count: 0 }}
        />
      </div>
    );
  }

  return (
    <div className='diagnosis-table'>
      {recordsArray.map((record) => {
        const supplierTypes = Object.keys(record.offers);
        const allSuppliers: Array<{
          type: string;
          supplierId: string;
          offers: Array<{ offerId: string; offer: OfferDebugData }>;
          firstOffer: OfferDebugData;
        }> = [];

        supplierTypes.forEach((supplierType) => {
          const suppliers = record.offers[supplierType];
          Object.entries(suppliers).forEach(([supplierId, supplierOffers]) => {
            const offerEntries = Object.entries(supplierOffers).map(
              ([offerId, offer]) => ({ offerId, offer }),
            );
            if (offerEntries.length > 0) {
              allSuppliers.push({
                type: supplierType,
                supplierId,
                offers: offerEntries,
                firstOffer: offerEntries[0].offer,
              });
            }
          });
        });

        const totalRows = allSuppliers.reduce(
          (sum, supplier) => sum + supplier.offers.length,
          0,
        );
        if (!allSuppliers.length) {
          return null;
        }
        return (
          <table
            key={record.ean}
            className='diagnosis-table__table'
            cellSpacing='0'
            cellPadding='0'
          >
            <tbody>
              {allSuppliers.map((supplier, supplierIndex) => {
                const supplierRowSpan = supplier.offers.length;

                return supplier.offers.map((offerData, offerIndex) => {
                  const isFirstOfferInSupplier = offerIndex === 0;
                  const isFirstSupplier = supplierIndex === 0;
                  const isFirstOfferOverall =
                    isFirstSupplier && isFirstOfferInSupplier;

                  return (
                    <tr
                      key={`${supplier.supplierId}-${offerData.offerId}`}
                      className='diagnosis-table__row'
                    >
                      {isFirstOfferOverall && (
                        <td
                          className='diagnosis-table__cell diagnosis-table__cell--info'
                          rowSpan={totalRows}
                        >
                          <div className='diagnosis-table__info-content'>
                            <div className='diagnosis-table__info-row'>
                              <span>
                                <Trans i18nKey='app.common.product.ean' />
                              </span>
                              <span className='diagnosis-table__ean'>
                                {record.ean}
                              </span>
                            </div>
                            <div className='diagnosis-table__info-row'>
                              <span>
                                <Trans
                                  i18nKey='app.common.product.constructor_reference_value'
                                  values={{
                                    value: record.wyzReference,
                                  }}
                                />
                              </span>
                            </div>
                            <div className='diagnosis-table__info-row'>
                              <span>
                                <Trans i18nKey='app.common.product.dimension' />
                              </span>
                              <span>{record.dimension}</span>
                            </div>
                            <div className='diagnosis-table__info-row'>
                              <span>
                                <Trans i18nKey='app.common.product.profile' />
                              </span>
                              <span>{record.profile}</span>
                            </div>
                            <div className='diagnosis-table__info-row'>
                              <span>
                                <Trans i18nKey='app.common.product.brand' />
                              </span>
                              <span>{record.brand}</span>
                            </div>
                            <div className='diagnosis-table__info-row'>
                              <span>
                                <Trans i18nKey='app.purchase.debug.label.in_reference_frame' />
                              </span>
                              <span>
                                {record.inReferenceFrame ? (
                                  <Icon
                                    icon='check-box-circle'
                                    size='xs'
                                    color='green'
                                  />
                                ) : (
                                  '-'
                                )}
                              </span>
                            </div>
                          </div>
                        </td>
                      )}

                      {isFirstOfferInSupplier && (
                        <td
                          className='diagnosis-table__cell diagnosis-table__cell--supplier'
                          rowSpan={supplierRowSpan}
                        >
                          <div className='diagnosis-table__supplier-content'>
                            <div className='diagnosis-table__supplier-type'>
                              {supplier.type}
                            </div>
                            <div className='diagnosis-table__supplier-name'>
                              {supplier.firstOffer.supplier}{' '}
                              {`(Id: ${supplier.firstOffer.supplierId})`}
                            </div>
                            <div className='diagnosis-table__supplier-info'>
                              <div className='diagnosis-table__info-item'>
                                <span>
                                  <Trans i18nKey='app.common.status.orderable' />
                                </span>
                                {supplier.firstOffer.supplierIsOrderable ? (
                                  <Icon
                                    icon='check-box-circle'
                                    size='xs'
                                    color='green'
                                  />
                                ) : (
                                  <Icon
                                    icon='close-circle'
                                    size='xs'
                                    color='red'
                                  />
                                )}
                              </div>
                              <div className='diagnosis-table__info-item'>
                                <span>
                                  <Trans i18nKey='app.common.status.bestWyz' />
                                </span>
                                {offerData.offer.selectedInBestWyz ? (
                                  <Icon
                                    icon='check-box-circle'
                                    size='xs'
                                    color='green'
                                  />
                                ) : (
                                  <Icon
                                    icon='close-circle'
                                    size='xs'
                                    color='red'
                                  />
                                )}
                              </div>

                              <div className='diagnosis-table__info-item'>
                                <span>
                                  <Trans i18nKey='app.common.supplier_types.central_stock' />
                                </span>
                                {supplier.firstOffer.supplierIsCentralStock ? (
                                  <Icon
                                    icon='check-box-circle'
                                    size='xs'
                                    color='green'
                                  />
                                ) : (
                                  <Icon
                                    icon='close-circle'
                                    size='xs'
                                    color='red'
                                  />
                                )}
                              </div>
                              <div className='diagnosis-table__info-item'>
                                <span>
                                  <Trans i18nKey='app.purchase.debug.label.get_stock_price' />
                                </span>
                                {supplier.firstOffer.supplierGetStockPrice ? (
                                  <Icon
                                    icon='check-box-circle'
                                    size='xs'
                                    color='green'
                                  />
                                ) : (
                                  <Icon
                                    icon='close-circle'
                                    size='xs'
                                    color='red'
                                  />
                                )}
                              </div>
                              <div className='diagnosis-table__info-item'>
                                <span>
                                  <Trans i18nKey='app.common.rank' />
                                </span>
                                <span>{supplier.firstOffer.supplierRank}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                      )}

                      <td
                        className={`diagnosis-table__cell diagnosis-table__cell--offer ${offerData.offer.context.errors ? 'bg-danger' : 'bg-success'}`}
                      >
                        <div className='diagnosis-table__offer-content'>
                          <div className='diagnosis-table__price-row'>
                            <span>
                              <Trans i18nKey='app.common.price.bf' />
                            </span>
                            <ColoredPrice price={record.bfPrice} />{' '}
                          </div>
                          <div className='diagnosis-table__price-row'>
                            <span>
                              <Trans i18nKey='app.common.price_d' />
                            </span>
                            <span>
                              <ColoredPrice
                                price={
                                  offerData.offer.distributionBuyingPrice.base
                                }
                              />{' '}
                              {'|'} <Trans i18nKey='app.common.price_c' />{' '}
                              <ColoredPrice
                                price={
                                  offerData.offer.distributionSellingPrice.base
                                }
                              />
                            </span>
                          </div>
                          <div className='diagnosis-table__price-groups'>
                            {(['g1', 'g2', 'g3', 'g4', 'g5'] as const).map(
                              (group, index) => (
                                <span key={group}>
                                  {index > 0 && ' | '} {`CG${index + 1} :`}
                                  {offerData.offer.distributionSellingPrice[
                                    group
                                  ]?.toFixed(2) || '-'}
                                  {CURRENCY}
                                </span>
                              ),
                            )}
                          </div>
                          <div className='diagnosis-table__price-groups'>
                            {(['g1', 'g2', 'g3', 'g4', 'g5'] as const).map(
                              (group, index) => (
                                <span key={group}>
                                  {index > 0 && ' | '}
                                  {`DG${index + 1} :`}
                                  {offerData.offer.distributionBuyingPrice[
                                    group
                                  ]?.toFixed(2) || '-'}
                                  {CURRENCY}
                                </span>
                              ),
                            )}
                          </div>
                          <div className='diagnosis-table__price-row'>
                            <span>
                              <Trans i18nKey='app.purchase.quote_search_page.product.stock.table.margin' />
                            </span>
                            <span>
                              <ColoredPrice price={offerData.offer.margin} />
                            </span>
                          </div>
                          <div className='diagnosis-table__price-row'>
                            <span>
                              <Trans i18nKey='app.purchase.debug.label.source_pricing' />
                            </span>
                            <span>
                              <Trans
                                i18nKey={
                                  DEBUG_PRICING_SOURCE_LOOKUP_LABEL[
                                    offerData.offer.pricingSource
                                  ]
                                }
                              />
                            </span>
                          </div>
                          <div className='diagnosis-table__price-row'>
                            <span>
                              <Trans i18nKey='app.common.supplier.invoiceable' />
                            </span>
                            {offerData.offer.supplierIsInvoiceable ? (
                              <Icon
                                icon='check-box-circle'
                                size='xs'
                                color='green'
                              />
                            ) : (
                              <Icon icon='close-circle' size='xs' color='red' />
                            )}
                          </div>
                          <div className='diagnosis-table__price-row'>
                            <span>
                              <Trans i18nKey='app.common.quantity' />
                            </span>
                            <span
                              className={clsx(
                                (offerData.offer.availableQuantity ?? 0) > 1
                                  ? 'text-green-1'
                                  : 'text-red',
                                'font-bold',
                              )}
                            >
                              {offerData.offer.availableQuantity}
                            </span>
                          </div>
                          <div className='diagnosis-table__price-row'>
                            <span>
                              <Trans i18nKey='app.common.orders.delivery_date' />
                            </span>
                            <span>
                              <FormatDate
                                date={offerData.offer.deliveryDate}
                                format='DMY'
                              />
                            </span>
                          </div>
                          <div className='diagnosis-table__price-row'>
                            <span>
                              <Trans i18nKey='app.purchase.debug.label.errors' />
                            </span>
                            <span>
                              {offerData.offer.context.errors
                                ? offerData.offer.context.errors.join(', ')
                                : '-'}
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        );
      })}
    </div>
  );
};
